import { openCartDrawer } from "../lib/cartApi";
import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ShoppingCart, User, ChevronDown, LogOut, Package, FileText, LayoutGrid, Menu, Sparkles } from "lucide-react";

const MobileMenu = lazy(() => import("./MobileMenu"));  // radix sheet only loads when the menu opens
import { BrandLogo } from "@/lib/brand";
import { getCategories, getHomeConfig } from "../lib/catalogueApi";
import { MegaMenu } from "./MegaMenu";

// T54 #11: Admin > Content > Navigation "Mega menu / Classic" (classic = the pre-T54 menu, unchanged)
export function useNavStyle() {
  const [s, setS] = useState(null);
  useEffect(() => { getHomeConfig().then((c) => setS(c.nav_style || "mega")).catch(() => setS("classic")); }, []);
  return s;
}
import { SearchSuggest } from "./SearchSuggest";
import { PAY_PATH, openAssistant } from "../lib/assistantApi";

const ctl = "inline-flex h-11 items-center gap-2 rounded-xl px-3 text-sm font-semibold transition-colors hover:bg-[var(--brand-primary-tint)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-focus)] active:bg-[var(--brand-primary)] active:text-white";

export function useTopCategories(open) {
  const [cats, setCats] = useState(null);
  useEffect(() => {
    if (!open || cats) return;
    getCategories().then((d) => setCats((d.categories || []).filter((c) => !c.parent_id && c.count !== 0).sort((a, b) => a.display_name.localeCompare(b.display_name)))).catch(() => setCats([]));
  }, [open, cats]);
  return cats;
}

function BrowseMenu() {
  const [open, setOpen] = useState(false);
  const cats = useTopCategories(open);
  const box = useRef(null);
  const loc = useLocation();
  useEffect(() => setOpen(false), [loc.pathname, loc.search]);
  useEffect(() => {
    const off = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    const esc = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", off);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", off); document.removeEventListener("keydown", esc); };
  }, []);
  return (
    <div className="relative" ref={box}>
      <button type="button" data-testid="nav-browse" aria-expanded={open} aria-haspopup="true" onClick={() => setOpen(!open)}
        className={`${ctl} border border-slate-300 ${open ? "bg-[var(--brand-primary)] text-white hover:bg-[var(--brand-primary)]" : ""}`}>
        <LayoutGrid size={18} aria-hidden="true" /> Browse products <ChevronDown size={15} className={`transition-transform ${open ? "rotate-180" : ""}`} aria-hidden="true" />
      </button>
      {open && (
        <div data-testid="browse-menu" className="absolute left-0 top-full z-50 mt-2 w-[min(760px,90vw)] rounded-2xl border border-slate-200 bg-white p-4 shadow-2xl">
          <Link to="/products" data-testid="nav-products" className="mb-3 flex items-center justify-between rounded-xl bg-[var(--brand-primary-tint)] px-4 py-3 text-sm font-bold text-[var(--brand-primary)] hover:bg-[var(--brand-primary)] hover:text-white">
            All products <span aria-hidden="true">→</span>
          </Link>
          <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 sm:grid-cols-3" role="list">
            {(cats || []).map((c) => (
              <Link key={c.slug} role="listitem" to={`/c/${c.slug}`} data-testid={`browse-cat-${c.slug}`}
                className="rounded-lg px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 hover:text-[var(--brand-link)]">{c.display_name}</Link>))}
            {!cats && <div className="col-span-3 py-6 text-center text-sm text-slate-400">Loading categories…</div>}
          </div>
        </div>)}
    </div>
  );
}

function AccountButton({ customer, logout }) {
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  if (!customer) return (
    <Link to="/account/auth" data-testid="nav-signin" className={ctl}><User size={20} aria-hidden="true" /><span>Sign in</span></Link>
  );
  return (
    <div className="relative">
      <button type="button" data-testid="account-menu-trigger" aria-expanded={open} onClick={() => setOpen((o) => !o)} onBlur={() => setTimeout(() => setOpen(false), 150)} className={ctl}>
        <User size={20} aria-hidden="true" /><span className="max-w-[110px] truncate">{customer.display_name || customer.email.split("@")[0]}</span><ChevronDown size={14} aria-hidden="true" />
      </button>
      {open && (
        <div data-testid="account-menu" className="rs-pop absolute right-0 z-50 mt-2 w-52 rounded-xl border border-slate-100 bg-white py-2 text-[var(--brand-primary)] shadow-xl">
          <Link to="/account" data-testid="menu-account" className="flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-slate-50"><User size={15} /> My account</Link>
          <Link to="/account/orders" data-testid="menu-orders" className="flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-slate-50"><Package size={15} /> Orders</Link>
          <Link to="/account/quotes" data-testid="menu-quotes" className="flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-slate-50"><FileText size={15} /> Quotes</Link>
          <button data-testid="menu-logout" onMouseDown={async () => { setOpen(false); await logout(); nav("/"); }} className="flex w-full items-center gap-2 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50"><LogOut size={15} /> Sign out</button>
        </div>)}
    </div>
  );
}

// T70: on checkout / payment the assistant is a small icon inside the header row (in layout, so it can't cover the form)
function PayAssistIcon() {
  const loc = useLocation();
  if (!PAY_PATH.test(loc.pathname)) return null;
  return (
    <button type="button" data-testid="asst-fab" data-variant="icon" aria-label="Ask Rhyte AI" onClick={() => openAssistant({ page: "checkout" })}
      className={`${ctl} w-11 justify-center px-0`}><Sparkles size={20} aria-hidden="true" /></button>
  );
}

function CartButton({ count, label = true }) {
  return (
    <a href="/cart" data-testid="nav-cart" className={ctl} aria-label={`Cart, ${count} item${count === 1 ? "" : "s"}`} aria-haspopup="dialog"
      onClick={(e) => { if (e.button || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return; e.preventDefault(); openCartDrawer(); }}>
      <span className="relative">
        <ShoppingCart size={21} aria-hidden="true" />
        {count > 0 && <span data-testid="nav-cart-count" className="absolute -right-2.5 -top-2 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-[var(--brand-accent)] px-1 text-[10px] font-extrabold text-white ring-2 ring-white">{count > 99 ? "99+" : count}</span>}
      </span>
      {label && <span className="hidden xl:inline">Cart</span>}
    </a>
  );
}

// T12.c header: white, search is the visual focus; every control >= 44px with hover/focus/active states.
export function StoreHeader({ count, customer, logout }) {
  const [menu, setMenu] = useState(false);
  useEffect(() => { const h = () => setMenu(true); window.addEventListener("rs:open-menu", h); return () => window.removeEventListener("rs:open-menu", h); }, []);
  const navStyle = useNavStyle();
  const search = "h-12 w-full rounded-xl border-2 border-slate-300 bg-white pl-4 pr-28 text-[15px] text-[var(--brand-primary)] outline-none transition-colors placeholder:text-slate-400 hover:border-slate-400 focus:border-[var(--brand-accent)] focus:ring-4 focus:ring-[var(--brand-accent-tint)]";
  return (
    <>
      <div className="hidden h-8 bg-[var(--brand-primary)] text-[12px] text-slate-200 md:block" data-testid="utility-bar">
        <div className="mx-auto flex h-full max-w-6xl items-center gap-5 px-5">
          <span>Australian IT hardware · Prices in AUD incl. GST</span>
          <span className="ml-auto">Business quotes: <a href="mailto:sales@rhytestepit.com.au" className="font-semibold text-white hover:underline">sales@rhytestepit.com.au</a></span>
          <button type="button" data-testid="nav-help-choose" onClick={() => openAssistant({ page: "header" })} className="font-semibold text-white hover:underline">Ask Rhyte AI</button>
          <Link to="/ex-demo-clearance" data-testid="nav-clearance" className="font-semibold text-white hover:underline">Ex-Demo &amp; Clearance</Link>
          <Link to="/procurement" data-testid="nav-procurement" className="font-semibold text-white hover:underline">Procurement</Link>
          <Link to="/lists" data-testid="nav-lists" className="font-semibold text-white hover:underline">Lists</Link>
        </div>
      </div>
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white text-[var(--brand-primary)] shadow-[0_1px_0_rgba(15,23,42,.04),0_6px_16px_-12px_rgba(15,23,42,.25)]">
        <div className="mx-auto flex h-[60px] max-w-6xl items-center gap-3 px-3 md:h-[72px] md:gap-4 md:px-5">
          <button type="button" className={`${ctl} w-11 justify-center px-0 lg:hidden`} aria-label="Open menu" data-testid="mobile-menu-open" onClick={() => setMenu(true)}><Menu size={22} /></button>
          <Link to="/" data-testid="store-logo" aria-label="Rhyte Step IT home" className="flex shrink-0 items-center rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-focus)]">
            <BrandLogo height={40} testid="store-logo-mark" />
          </Link>
          <div className="hidden lg:block" data-nav-style={navStyle || "loading"}>{navStyle === "classic" || !navStyle ? <BrowseMenu /> : <MegaMenu ctl={ctl} />}</div>
          <div className="hidden min-w-0 flex-1 md:block"><SearchSuggest inputClass={search} testid="header-search" big /></div>
          <nav className="ml-auto flex shrink-0 items-center gap-1 md:ml-0" aria-label="Account">
            <Link to="/account/quotes" data-testid="nav-quotes" className={`${ctl} hidden lg:inline-flex`}><FileText size={20} aria-hidden="true" /><span className="hidden xl:inline">Quotes</span></Link>
            <span className="hidden md:block"><AccountButton customer={customer} logout={logout} /></span>
            <PayAssistIcon />
            <CartButton count={count} />
          </nav>
        </div>
        <div className="border-t border-slate-100 px-3 pb-2.5 pt-2 md:hidden" data-testid="mobile-search-row">
          <SearchSuggest inputClass={search.replace("h-12", "h-11")} testid="mobile-search" big />
        </div>
      </header>
      {menu && <Suspense fallback={null}><MobileMenu open={menu} setOpen={setMenu} customer={customer} navStyle={navStyle} /></Suspense>}
    </>
  );
}
